import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import "./App.css";

interface Message {
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: Date;
}

interface ApiConfig {
  hasEnvironmentApiKey: boolean;
  requiresApiKeyInput: boolean;
}

const MODELS = [
  { id: "gpt-4.1-nano", name: "GPT-4.1 Nano" },
  { id: "gpt-4.1-mini", name: "GPT-4.1 Mini" },
  { id: "gpt-4o-mini", name: "GPT-4o Mini" },
  { id: "gpt-3.5-turbo", name: "GPT-3.5 Turbo" },
];

function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [showApiKeyInput, setShowApiKeyInput] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState(
    "You are a helpful assistant."
  );
  const [selectedModel, setSelectedModel] = useState("gpt-4.1-nano");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load API key and system prompt from sessionStorage on component mount
  useEffect(() => {
    const savedApiKey = sessionStorage.getItem('openai_api_key');
    if (savedApiKey && validateApiKey(savedApiKey)) {
      setApiKey(savedApiKey);
      setShowApiKeyInput(false);
    }

    const savedSystemPrompt = sessionStorage.getItem('system_prompt');
    if (savedSystemPrompt) {
      setSystemPrompt(savedSystemPrompt);
    }
  }, []);

  const validateApiKey = (key: string): boolean => {
    return key.trim().startsWith("sk-") && key.trim().length > 20;
  };

  const callOpenAIDirectly = async (
    messages: any[],
    apiKey: string,
    model: string
  ) => {
    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: model,
        messages: messages,
        max_tokens: 4096,
        temperature: 0.5,
        frequency_penalty: 0.1,
        presence_penalty: 0.1,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401) {
        throw new Error("Invalid API key. Please check your credentials.");
      } else if (response.status === 429) {
        throw new Error(
          "Rate limit exceeded. Please wait a moment and try again."
        );
      } else if (response.status === 400) {
        throw new Error(
          "Invalid request. Please check your input and try again."
        );
      } else {
        throw new Error(
          errorData.error?.message || `API Error: ${response.status}`
        );
      }
    }

    const data = await response.json();
    return (
      data.choices[0]?.message?.content ||
      "I apologize, but I could not generate a response at this time."
    );
  };

  const processFiles = async (files: File[]): Promise<string> => {
    const fileDescriptions = [];

    for (const file of files) {
      try {
        const fileContent = await file.text();
        fileDescriptions.push(
          `File: ${file.name}\nContent: ${fileContent.slice(0, 2000)}${
            fileContent.length > 2000 ? "..." : ""
          }`
        );
      } catch (error) {
        console.error("Error processing file:", error);
        fileDescriptions.push(`File: ${file.name} (could not read content)`);
      }
    }

    return fileDescriptions.join("\n\n");
  };

  const sendMessage = async () => {
    if (!inputValue.trim() && uploadedFiles.length === 0) return;

    if (!validateApiKey(apiKey)) {
      alert("Please enter a valid OpenAI API key (starts with 'sk-')");
      setShowApiKeyInput(true);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue.trim() || "Uploaded files",
      role: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      // Prepare conversation history
      const conversationHistory = [];

      // Add system prompt if provided
      if (systemPrompt && systemPrompt.trim()) {
        conversationHistory.push({
          role: "system",
          content: systemPrompt.trim(),
        });
      }

      // Add previous messages (limit to last 10)
      for (const msg of messages.slice(-10)) {
        if (msg && msg.role && msg.content && typeof msg.content === "string") {
          conversationHistory.push({
            role: msg.role === "user" ? "user" : "assistant",
            content: msg.content.slice(0, 4000),
          });
        }
      }

      // Add the current message with files
      let messageContent = inputValue.trim();

      if (uploadedFiles.length > 0) {
        const fileContent = await processFiles(uploadedFiles);
        messageContent = messageContent
          ? `${messageContent}\n\nUploaded files:\n${fileContent}`
          : `Uploaded files:\n${fileContent}`;
      }

      conversationHistory.push({
        role: "user",
        content: messageContent,
      });

      // Call OpenAI API directly
      const assistantResponse = await callOpenAIDirectly(
        conversationHistory,
        apiKey,
        selectedModel
      );

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: assistantResponse,
        role: "assistant",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setUploadedFiles([]);
    } catch (error) {
      console.error("Error:", error);

      let errorContent =
        "I apologize, but I encountered an error processing your request.";

      if (error instanceof Error) {
        if (error.message.includes("Invalid API key")) {
          errorContent =
            "Invalid API key. Please check your OpenAI API key and try again.";
          setShowApiKeyInput(true);
        } else if (error.message.includes("Rate limit")) {
          errorContent =
            "Rate limit exceeded. Please wait a moment and try again.";
        } else if (
          error.message.includes("fetch") ||
          error.message.includes("Network")
        ) {
          errorContent =
            "Network error. Please check your connection and try again.";
        } else {
          errorContent = `Error: ${error.message}`;
        }
      }

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: errorContent,
        role: "assistant",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleApiKeySubmit = () => {
    if (validateApiKey(apiKey)) {
      sessionStorage.setItem('openai_api_key', apiKey);
      setShowApiKeyInput(false);
    } else {
      alert("Please enter a valid OpenAI API key (starts with 'sk-')");
    }
  };

  const clearChat = () => {
    setMessages([]);
    setUploadedFiles([]);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const clearApiKey = () => {
    sessionStorage.removeItem('openai_api_key');
    setApiKey("");
    setShowApiKeyInput(true);
  };

  // Show API key input
  if (showApiKeyInput) {
    return (
      <div className="api-key-screen">
        <div className="api-key-container">
          <div className="api-key-header">
            <h1>Aiology Playground</h1>
            <p>Enter your OpenAI API key to continue</p>
          </div>
          <div className="api-key-form">
            <div className="input-group">
              <label htmlFor="apiKey">API Key</label>
              <input
                id="apiKey"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-..."
                className="api-key-input"
                onKeyPress={(e) => e.key === "Enter" && handleApiKeySubmit()}
              />
            </div>
            <button
              onClick={handleApiKeySubmit}
              disabled={!validateApiKey(apiKey)}
              className="continue-button"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <h1 className="header-title">Aiology Playground</h1>
          <div className="header-actions">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="settings-button"
              title="Settings"
            >
              ⚙️
            </button>
            {messages.length > 0 && (
              <button onClick={clearChat} className="clear-button">
                Clear
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Settings Panel */}
      {showSettings && (
        <div className="settings-panel">
          <div className="settings-content">
            <div className="settings-section">
              <label className="settings-label">System Prompt</label>
              <textarea
                value={systemPrompt}
                onChange={(e) => {
                  setSystemPrompt(e.target.value);
                  sessionStorage.setItem('system_prompt', e.target.value);
                }}
                className="system-prompt-input"
                placeholder="Enter system prompt..."
                rows={3}
              />
            </div>
            <div className="settings-section">
              <label className="settings-label">Model</label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="model-select"
              >
                {MODELS.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Chat Area */}
      <div className="chat-container">
        <div className="chat-content">
          {/* Empty state */}
          {messages.length === 0 && (
            <div className="empty-state">
              <h2>Welcome to Aiology Playground</h2>
              <p>Start a conversation with AI</p>
              <div className="model-info">
                <span>
                  Using: {MODELS.find((m) => m.id === selectedModel)?.name}
                </span>
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((message) => (
            <div key={message.id} className="message-group">
              {/* Role label */}
              <div className="message-role">
                {message.role === "user" ? "You" : "Assistant"}
              </div>

              {/* Message content */}
              <div
                className={`message-bubble ${
                  message.role === "user" ? "user-message" : "assistant-message"
                }`}
              >
                <div className="message-content">
                  {message.role === "assistant" ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                      components={{
                        // Custom styling for code blocks
                        code: ({
                          node,
                          className,
                          children,
                          ...props
                        }: any) => {
                          const inline = !className;
                          if (inline) {
                            return (
                              <code className="inline-code" {...props}>
                                {children}
                              </code>
                            );
                          }
                          return (
                            <pre className="code-block">
                              <code className={className} {...props}>
                                {children}
                              </code>
                            </pre>
                          );
                        },
                        // Custom styling for lists
                        ul: ({ children }: any) => (
                          <ul className="markdown-list">{children}</ul>
                        ),
                        ol: ({ children }: any) => (
                          <ol className="markdown-list ordered">{children}</ol>
                        ),
                        li: ({ children }: any) => (
                          <li className="markdown-list-item">{children}</li>
                        ),
                        // Custom styling for blockquotes
                        blockquote: ({ children }: any) => (
                          <blockquote className="markdown-quote">
                            {children}
                          </blockquote>
                        ),
                        // Custom styling for tables
                        table: ({ children }: any) => (
                          <table className="markdown-table">{children}</table>
                        ),
                        th: ({ children }: any) => (
                          <th className="markdown-th">{children}</th>
                        ),
                        td: ({ children }: any) => (
                          <td className="markdown-td">{children}</td>
                        ),
                      }}
                    >
                      {message.content
                        .replace(/\\\(/g, "$")
                        .replace(/\\\)/g, "$")
                        .replace(/\\\[/g, "$$")
                        .replace(/\\\]/g, "$$")}
                    </ReactMarkdown>
                  ) : (
                    message.content
                  )}
                </div>
              </div>

              {/* Timestamp */}
              <div className="message-time">
                {message.timestamp.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isLoading && (
            <div className="message-group">
              <div className="message-role">Assistant</div>
              <div className="message-bubble assistant-message">
                <div className="typing-indicator">
                  <div className="typing-dots">
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                  <span>Thinking...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="input-area">
        <div className="input-container">
          {/* File Upload Preview */}
          {uploadedFiles.length > 0 && (
            <div className="uploaded-files">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="file-preview">
                  <span className="file-name">📎 {file.name}</span>
                  <button
                    onClick={() => removeFile(index)}
                    className="remove-file"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="input-wrapper">
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              multiple
              accept="image/*,.pdf,.txt,.doc,.docx,.csv,.json"
              style={{ display: "none" }}
            />

            {/* File upload button */}
            <button
              onClick={triggerFileUpload}
              className="file-upload-button"
              disabled={isLoading}
              title="Upload files"
            >
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                />
              </svg>
            </button>

            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="What's on your mind?"
              className="message-input"
              rows={1}
              disabled={isLoading}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = "auto";
                target.style.height = target.scrollHeight + "px";
              }}
            />
            <button
              onClick={sendMessage}
              disabled={
                (!inputValue.trim() && uploadedFiles.length === 0) || isLoading
              }
              className="send-button"
            >
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
